import React, { useCallback, useEffect, useMemo } from 'react'
import { FormikProps, useFormikContext } from 'formik'

import {
  ScheduleType,
  FieldMetadata,
  FieldMetadataField,
} from '../../../../modules/privateOffer/types'

import {
  DatePickerV2,
  LabraToolTipV2,
  BoxV4,
  RadioButtonsV2,
  RadioButtonType,
  RadioButtonGroupMapping,
} from '@labrav/react-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'

import { useSelector } from '../../../../store'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import { useStyles } from './AuthorizationDuration.styles'
import { AgreementDurationType, DiscountType } from '../../../../types/flyout'
import {
  labraUIDateFormatForm,
  addOneDayToDate,
  calculateDurationInMonths,
} from '../../helpers'
import { isEmpty } from 'lodash'
import { getAmiInstallmentsBasedOnSchedule } from '../../../../utils/amiPrivateOfferUtil'
import type { AuthorizationDurationProps } from '../../types'

export type { AuthorizationDurationProps }

export const AuthorizationDuration: React.FC<AuthorizationDurationProps> = ({
  product,
}) => {
  const { classes } = useStyles()
  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        product?.productId as string
      ]?.fieldMetadata
  )
  const isAuthorizationEndDateHidden = (
    fieldMetadata?.agreementExpirationDate as FieldMetadataField
  )?.hidden
  const isAuthorizationEndDateReadOnly = (
    fieldMetadata?.agreementExpirationDate as FieldMetadataField
  )?.readOnly

  const isAuthorizationDurationHidden = (
    fieldMetadata?.agreementDurationType as FieldMetadataField
  )?.hidden
  const isAuthorizationDurationReadOnly = (
    fieldMetadata?.agreementDurationType as FieldMetadataField
  )?.readOnly

  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  const formikProps = useFormikContext() as FormikProps<Record<string, unknown>>

  const handleAgreementDurationTypeChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const clickedKey = e.target.value

    if (clickedKey === AgreementDurationType.NO_SET_TIME) {
      await setFieldValue('agreementExpirationDate', '')
    }
    if (!clickedKey) {
      await setFieldValue('agreementDurationType', values.agreementDurationType)
      return
    }
    await setFieldValue('agreementDurationType', clickedKey)
  }
  const isNoSetTimeDuration = useMemo(
    () => values.agreementDurationType === AgreementDurationType.NO_SET_TIME,
    [values.agreementDurationType]
  )
  const authorizationEndDate = {
    title: 'Authorization end date',
    humanReadableKey: 'Authorization end date',
    placeHolder: 'Authorization end date',
    id: 'agreementExpirationDate',
    isRequired: false,
    isReadOnly: isNoSetTimeDuration || isAuthorizationEndDateReadOnly,
    isCustom: false,
    dataType: 'date',
    minDate: 'today',
  }
  const isResaleAuthorizationDurationDisabled =
    values.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS

  useEffect(() => {
    if (
      isResaleAuthorizationDurationDisabled &&
      !isAuthorizationDurationReadOnly &&
      values.agreementDurationType !== AgreementDurationType.SINGLE_USE
    ) {
      setFieldValue('agreementDurationType', AgreementDurationType.SINGLE_USE)
    }
  }, [
    isResaleAuthorizationDurationDisabled,
    isAuthorizationDurationReadOnly,
    values.agreementDurationType,
    setFieldValue,
  ])

  const authorizationDurationMapping: RadioButtonGroupMapping[] = [
    {
      key: AgreementDurationType.SINGLE_USE,
      label: 'Single use',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Reseller can create a single channel offer for the product using this authorization before the authorization end date specified below.',
    },
    {
      key: AgreementDurationType.SPECIFIC_TIME,
      label: 'Specific time duration',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Reseller can create a multiple channel offers for the product using this authorization until the end date specified below. This option is not available for Installment plan.',
      disabled: isResaleAuthorizationDurationDisabled,
    },
    {
      key: AgreementDurationType.NO_SET_TIME,
      label: 'No set time duration',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Reseller can create a multiple channel offers for the product using this authorization until you manually deactivate the authorization. This option is not available for Installment plan.',
      disabled: isResaleAuthorizationDurationDisabled,
    },
  ]

  const regenerateInstallments = useCallback(
    (
      containerOfferServiceLength: string | number,
      unitTime: string,
      minimumDate: string
    ) => {
      const durationInMonths = calculateDurationInMonths(
        containerOfferServiceLength,
        unitTime
      )
      const paymentSchedule =
        values?.flexiblePaymentType || ScheduleType.MONTHLY
      const offerStartDate = minimumDate
      const tcv = values?.tcv
      // Only regenerate if not CUSTOM and we have required values
      if (paymentSchedule !== ScheduleType.CUSTOM && durationInMonths && tcv) {
        const newInstallments = getAmiInstallmentsBasedOnSchedule({
          tcv: tcv || 0,
          durationInMonths: durationInMonths || 0,
          paymentSchedule: paymentSchedule as ScheduleType,
          now: offerStartDate as string,
        })

        // Set first installment paymentDate from firstInstallmentDate if available (for AMI)
        if (values?.firstInstallmentDate && newInstallments.length > 0) {
          newInstallments[0].paymentDate = values?.firstInstallmentDate
        }
        if (!isEmpty(errors.firstInstallmentDate)) {
          setFieldTouched('installmentInfo[0].paymentDate', true)
        }
        //do touched
        setFieldValue('installmentInfo', newInstallments)
      }
    },
    [
      values?.flexiblePaymentType,
      values?.offerStartDate,
      values?.firstInstallmentDate,
      values?.tcv,
      setFieldValue,
      values.contractDurationInMonthsForEndCustomer,
      values.contractDurationTimeUnit,
      values.agreementExpirationDate,
    ]
  )

  return (
    <div data-testid="flexible-section">
      <BoxV4
        heading={'Authorization duration'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        {!isAuthorizationDurationHidden && (
          <div data-testid="ami-pricing-section">
            <div
              data-testid="ami-pricing-model-options"
              className={classes.radioWithRadio}
            >
              <RadioButtonsV2
                radioButtonMappings={authorizationDurationMapping.map(
                  option => ({
                    ...option,
                    disabled:
                      isAuthorizationDurationReadOnly || option.disabled,
                  })
                )}
                checkedValue={{
                  key: values.agreementDurationType || '',
                  value: values.agreementDurationType || '',
                }}
                handleChange={handleAgreementDurationTypeChange}
                gapBetween="24px"
              />
            </div>
          </div>
        )}
        {!isAuthorizationEndDateHidden && (
          <div className={classes.authorizationDurationContainer}>
            <div className={classes.authorizationDurationInputWidth}>
              <LabraToolTipV2
                tooltipPlacement="bottom-start"
                description={
                  isNoSetTimeDuration
                    ? 'Reseller can create a multiple channel offers for the product using this authorization until you manually deactivate the authorization.'
                    : ''
                }
              >
                <div className={classes.inputWidth}>
                  <DatePickerV2
                    format={labraUIDateFormatForm}
                    views={['year', 'month', 'day']}
                    field={authorizationEndDate}
                    key={'agreementExpirationDate'}
                    showErrorHelper={
                      !!touched.agreementExpirationDate &&
                      !isEmpty(errors?.agreementExpirationDate)
                    }
                    fieldId={'agreementExpirationDate'}
                    formik={
                      formikProps as unknown as FormikProps<
                        Record<string, unknown>
                      >
                    }
                    handleDateChange={async date => {
                      await setFieldTouched('agreementExpirationDate', true)
                      await setFieldValue('agreementExpirationDate', date)

                      const nextMinDate = addOneDayToDate(date as string)
                      regenerateInstallments(
                        values.contractDurationInMonthsForEndCustomer as number,
                        values.contractDurationTimeUnit as string,
                        nextMinDate as string
                      )
                      setFieldTouched('installmentInfo[0].paymentDate', true)
                    }}
                    errorHelperText={
                      (!!touched?.agreementExpirationDate &&
                        errors?.agreementExpirationDate) ||
                      ''
                    }
                    size="small"
                  />
                </div>
                <div className={classes.inputWidth}></div>
              </LabraToolTipV2>
            </div>
          </div>
        )}
      </BoxV4>
    </div>
  )
}
